// =============================================================================
// ui/media/ShotCarousel.tsx — one attached image at a time, beside the stats.
// -----------------------------------------------------------------------------
// Relocated from the old client's `renderCarousel` (index.html:25859-25887) and
// the `.tw-shot` markup above it (:25595-25612).
//
// ── 🚨 WHY THIS EXISTS WHEN THE ATTACHMENT GRID ALREADY DID ──
//
// The desktop logger captures a PNG of the trader's own chart when a position
// flattens and posts it to `ingest-screenshot`, which appends the filename to
// that trade's `screenshots[]`. It IS the picture of the trade. Beta shows it
// next to the numbers the moment the trade opens; this client had it only in
// `ScreenshotGrid` on the sixth tab, which is a surface for ATTACHING files
// rather than for looking at one.
//
// ⚠ NOT A REPLACEMENT FOR THE GRID. The grid adds, removes and reorders; this
// only looks. Two components, two jobs, one list.
//
// ── ⚠ THE CHART HALF OF BETA'S PANEL IS STILL DEFERRED ──
//
// Beta's `.tw-shot` is a two-mode panel with a `Chart | Screenshots` toggle. The
// chart needs Lightweight Charts and the bars-fetch service, which this client
// does not load, so there is nothing for a toggle to toggle TO — and beta itself
// falls back to the screenshots side whenever the chart is unsupported
// (index.html:26519-26523), which is this client's permanent state.
//
// ▶ So there is no toggle here, and the trade form's deferred band still says
// the chart is not ported. The toggle arrives with the chart.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Lightbox } from './Lightbox';
import { useMediaObjectUrl } from './useMediaObjectUrl';

/**
 * The old client's caption rule, transcribed — including the fallback.
 *
 * ⚠ AND THE FALLBACK IS WHAT YOU WILL ACTUALLY SEE. MEASURED: nothing either
 * client writes carries a six-digit time group. The logger names files
 * `<date>-<contract>-auto-<hash>.<ext>` and beta's paste names them
 * `<date>-<contract>-N.<ext>`; neither matches, so both print the filename.
 *
 * ▶ KEPT ANYWAY, and deliberately not "improved". A nicer caption is a redesign
 * (D4 — relocated, not redesigned) and would put different words on the two
 * clients' screens for the same file. If the filename reads badly here, that is
 * a conversation about what the LOGGER names things.
 */
export function shotCaption(filename: string): string {
  const m = /-(\d{2})(\d{2})(\d{2})-/.exec(filename);
  return m ? `${m[1]}:${m[2]}:${m[3]}` : filename;
}

export interface ShotCarouselProps {
  /** The trade's `screenshots[]`, in stored order. */
  files: readonly string[];
}

export function ShotCarousel({ files }: ShotCarouselProps) {
  const [idx, setIdx] = useState(0);
  const [big, setBig] = useState(false);

  const n = files.length;

  /**
   * 🔴 THE LIST CAN SHRINK UNDER THIS COMPONENT, and beta guards the same case
   * (`if (shotIdx >= arr.length) shotIdx = 0`).
   *
   * This reads the DRAFT's list, so deleting an attachment two tabs away in
   * Notes & Media shortens it while this is mounted and holding an index into
   * the old one. Without the reset the panel renders `files[3]` of a
   * one-element array — `undefined`, which is a blank box rather than an image.
   */
  useEffect(() => {
    if (idx >= n && n > 0) setIdx(0);
  }, [idx, n]);

  const at = idx < n ? idx : 0;
  const current = n ? (files[at] ?? null) : null;
  const { url, error } = useMediaObjectUrl('screenshots', current);

  // ⚠ WRAPPING, both ways. `(i - 1 + n) % n` rather than `i - 1`: a bare
  // decrement reaches -1, and `files[-1]` is undefined — a blank panel instead
  // of the last image.
  const many = n > 1;
  const next = useCallback(() => { if (many) setIdx((i) => (i + 1) % n); }, [many, n]);
  const prev = useCallback(() => { if (many) setIdx((i) => (i - 1 + n) % n); }, [many, n]);

  // ── nothing attached ──────────────────────────────────────────────────────
  //
  // ⚠ NO CONTROLS AT ALL, not even expand — beta hides that one too
  // (`shotMax.style.display = arr.length ? '' : 'none'`). A button that opens an
  // empty lightbox is worse than no button.
  if (!n || !current) {
    return (
      <div className="tw-shot" data-testid="tw-shot">
        <div className="tw-screens-area">
          <div className="tw-shot-empty muted">No screenshots attached</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-shot" data-testid="tw-shot">
      <div className="tw-screens-area">
        <div className="tw-cap">{shotCaption(current)}</div>

        {many ? (
          <button
            type="button" className="tw-arrow l"
            aria-label="Previous screenshot" onClick={prev}
          >‹</button>
        ) : null}

        {/* ⚠ THE IMAGE IS A CONTROL TOO. Beta binds `openMax` to both the expand
            button and the image itself, and `cursor: zoom-in` on `.tw-shot-img`
            advertises it. A `<button>` wrapper would put a focusable control
            around a focusable control; the role and the key handler go on the
            image. */}
        {url ? (
          <img
            className="tw-shot-img" src={url} alt={current}
            role="button" tabIndex={0}
            onClick={() => setBig(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setBig(true); }
            }}
          />
        ) : (
          // ⚠ A ROW CAN OUTLIVE ITS OBJECT — these buckets have no backup, so
          // this is a state the user will actually meet. It gets the sentence
          // `useMediaObjectUrl` already composed rather than a broken image.
          <div className="tw-shot-empty muted">{error ?? 'Loading…'}</div>
        )}

        {many ? (
          <button
            type="button" className="tw-arrow r"
            aria-label="Next screenshot" onClick={next}
          >›</button>
        ) : null}

        <button
          type="button" className="tw-max"
          aria-label="Expand screenshot" title="Expand"
          onClick={() => setBig(true)}
        >⛶</button>

        {/* ⚠ HIDDEN ON A SINGLE IMAGE. "1 / 1" is not information, and every
            logger trade has exactly one. */}
        {many ? <div className="tw-counter">{at + 1} / {n}</div> : null}
      </div>

      {/* ⚠ `startIndex={at}`, not 0. Expanding means "show me THIS one bigger";
          opening at the start silently discards where the reader was. */}
      {big ? (
        <Lightbox
          files={[...files]} startIndex={at} bucket="screenshots"
          onClose={() => setBig(false)}
        />
      ) : null}
    </div>
  );
}
